import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Heart, ThumbsUp, HelpCircle, PartyPopper, Eye, MoreVertical, Edit, Trash2 } from 'lucide-react';
import { Post, communityService } from '../../services/communityService';
import { formatDistanceToNow } from 'date-fns';
import { ro } from 'date-fns/locale';

interface PostCardProps {
  post: Post;
  onReact?: (postId: string, type: string) => void;
  onDelete?: (postId: string) => void;
}

export default function PostCard({ post, onReact, onDelete }: PostCardProps) {
  const navigate = useNavigate();
  const storedUser = localStorage.getItem('user');
  const currentUserId = storedUser ? JSON.parse(storedUser).id : null;
  const isOwner = post.userId === currentUserId;

  const [menuOpen, setMenuOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getPostTypeInfo = (type: string) => {
    switch (type) {
      case 'QUESTION':
        return { icon: '❓', label: 'Întrebare', color: 'bg-blue-100 text-blue-800' };
      case 'EXPERIENCE':
        return { icon: '📖', label: 'Experiență', color: 'bg-purple-100 text-purple-800' };
      case 'VICTORY':
        return { icon: '🎉', label: 'Victorie', color: 'bg-green-100 text-green-800' };
      case 'TIP':
        return { icon: '💡', label: 'Sfat', color: 'bg-yellow-100 text-yellow-800' };
      case 'SUPPORT':
        return { icon: '💚', label: 'Suport', color: 'bg-pink-100 text-pink-800' };
      default:
        return { icon: '📝', label: 'General', color: 'bg-gray-100 text-gray-800' };
    }
  };

  const typeInfo = getPostTypeInfo(post.type);

  const userReaction = post.reactions && post.reactions.length > 0 
    ? post.reactions[0].type 
    : null;

  const handleReact = (e: React.MouseEvent, type: string) => {
    e.stopPropagation();
    if (onReact) {
      onReact(post.id, type);
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(false);
    navigate(`/community/posts/${post.id}/edit`);
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(false);

    if (!confirm('Sigur vrei să ștergi această postare? Acțiunea nu poate fi anulată.')) return;

    try {
      setDeleting(true);
      await communityService.deletePost(post.id);
      if (onDelete) {
        onDelete(post.id);
      }
    } catch (err: any) {
      alert(err.response?.data?.error || 'Eroare la ștergerea postării');
      setDeleting(false);
    }
  };

  const formatTimeAgo = (date: string) => {
    try {
      return formatDistanceToNow(new Date(date), { 
        addSuffix: true, 
        locale: ro 
      });
    } catch {
      return 'recent';
    }
  };

  if (deleting) {
    return null;
  }

  return (
    <div
      onClick={() => navigate(`/community/posts/${post.id}`)}
      className="bg-white rounded-lg border border-gray-200 hover:border-blue-300 transition-all cursor-pointer hover:shadow-md p-6"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
            {post.isAnonymous 
              ? '?'
              : (post.user.firstName?.[0] || 'U').toUpperCase()
            }
          </div>
          <div>
            <p className="font-semibold text-gray-900">
              {post.isAnonymous ? 'Anonim' : `${post.user.firstName} ${post.user.lastName}`}
            </p>
            <p className="text-sm text-gray-500">
              {formatTimeAgo(post.createdAt)}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${typeInfo.color}`}>
            {typeInfo.icon} {typeInfo.label}
          </span>

          {/* Owner actions menu */}
          {isOwner && (
            <div ref={menuRef} className="relative" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen(!menuOpen);
                }}
                className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                title="Opțiuni"
              >
                <MoreVertical className="w-4 h-4" />
              </button>

              {menuOpen && (
                <div className="absolute right-0 top-8 z-10 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[140px]">
                  <button
                    onClick={handleEdit}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                    Editează
                  </button>
                  <button
                    onClick={handleDelete}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Șterge
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Disease badge */}
      {post.disease && (
        <div className="mb-3">
          <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
            {post.disease.name}
          </span>
        </div>
      )}

      {/* Title */}
      {post.title && (
        <h3 className="text-lg font-bold text-gray-900 mb-2">
          {post.title}
        </h3>
      )}

      {/* Content */}
      <p className="text-gray-700 mb-4 line-clamp-3">
        {post.content}
      </p>

      {/* Images */}
      {post.imageUrls && post.imageUrls.length > 0 && (
        <div className="mb-4 grid grid-cols-2 gap-2">
          {post.imageUrls.slice(0, 4).map((url, index) => (
            <img
              key={index}
              src={url}
              alt={`Image ${index + 1}`}
              className="rounded-lg object-cover h-32 w-full"
            />
          ))}
        </div>
      )}

      {/* Stats & Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
        <div className="flex items-center gap-4 text-sm text-gray-500">
          <div className="flex items-center gap-1">
            <Eye className="w-4 h-4" />
            <span>{post.viewsCount}</span>
          </div>
          <div className="flex items-center gap-1">
            <MessageCircle className="w-4 h-4" />
            <span>{post.commentsCount}</span>
          </div>
          <div className="flex items-center gap-1">
            <Heart className="w-4 h-4" />
            <span>{post.likesCount}</span>
          </div>
        </div>

        {/* Reaction buttons */}
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => handleReact(e, 'LIKE')}
            className={`p-2 rounded-full transition-all ${
              userReaction === 'LIKE'
                ? 'bg-blue-100 text-blue-600'
                : 'hover:bg-gray-100 text-gray-600'
            }`}
            title="Like"
          >
            <ThumbsUp className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => handleReact(e, 'HEART')}
            className={`p-2 rounded-full transition-all ${
              userReaction === 'HEART'
                ? 'bg-red-100 text-red-600'
                : 'hover:bg-gray-100 text-gray-600'
            }`}
            title="Heart"
          >
            <Heart className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => handleReact(e, 'SUPPORT')}
            className={`p-2 rounded-full transition-all ${
              userReaction === 'SUPPORT'
                ? 'bg-green-100 text-green-600'
                : 'hover:bg-gray-100 text-gray-600'
            }`}
            title="Support"
          >
            <HelpCircle className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => handleReact(e, 'CELEBRATE')}
            className={`p-2 rounded-full transition-all ${
              userReaction === 'CELEBRATE'
                ? 'bg-yellow-100 text-yellow-600'
                : 'hover:bg-gray-100 text-gray-600'
            }`}
            title="Celebrate"
          >
            <PartyPopper className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
